import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";
import { api } from "../lib/api";
import { toast } from "sonner";
import { IssuanceSourceBadge } from "./IssuanceSourceBadge";

/**
 * Admin drawer showing the live event stream from Stripe Issuing for a
 * single card. Pulls from `/admin/cards/{id}/issuance-events` and renders:
 *   - Card meta (receiver, last4, balance, provider, ids)
 *   - Reverse-chronological audit feed of authorizations + transactions
 *
 * For closed-loop ("wallet") cards there's nothing to stream; the drawer
 * shows an explanatory empty-state instead.
 */
export function CardIssuanceDrawer({ open, onClose, cardId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !cardId) return undefined;
    setLoading(true);
    setError(null);
    setData(null);
    let alive = true;
    api
      .get(`/admin/cards/${cardId}/issuance-events`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Could not load events"); })
      .finally(() => { if (alive) setLoading(false); });
    // Auto-refresh every 8s while open so streaming auths land without a manual reload.
    const id = setInterval(() => {
      if (!alive) return;
      api.get(`/admin/cards/${cardId}/issuance-events`).then((r) => { if (alive) setData(r.data); }).catch(() => {});
    }, 8000);
    return () => { alive = false; clearInterval(id); };
  }, [open, cardId]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl bg-[#FBF9F6] border-l border-[#E5E0D8] overflow-y-auto"
        data-testid="card-issuance-drawer"
      >
        <SheetHeader>
          <SheetTitle className="text-[#0A1F16]">Card-issuance details</SheetTitle>
          <SheetDescription className="text-[#4A5C50] text-xs">
            Live authorizations and transactions for this card.
          </SheetDescription>
        </SheetHeader>

        {loading && !data && (
          <p className="text-sm text-[#4A5C50] mt-6" data-testid="card-issuance-drawer-loading">Loading…</p>
        )}
        {error && (
          <p className="text-sm text-rose-700 mt-6" data-testid="card-issuance-drawer-error">{error}</p>
        )}

        {data && (
          <>
            <CardMetaPanel card={data.card} />
            <EventStream events={data.events} card={data.card} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}


function CardMetaPanel({ card }) {
  return (
    <div
      className="mt-5 rounded-xl border border-[#E5E0D8] bg-white p-4 text-sm"
      data-testid="card-issuance-drawer-meta"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-[#0A1F16] truncate">{card.receiver_name || "—"}</p>
          <p className="text-xs text-[#4A5C50] mono truncate">{card.receiver_email || ""}</p>
        </div>
        <IssuanceSourceBadge provider={card.provider} network={card.network} />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <dt className="text-[#7A8B82] uppercase tracking-widest">Last 4</dt>
        <dd className="mono text-[#0A1F16]">{card.last4 ? `•••• ${card.last4}` : "—"}</dd>
        <dt className="text-[#7A8B82] uppercase tracking-widest">Balance</dt>
        <dd className="mono text-[#0A1F16]">${(card.balance_usd || 0).toFixed(2)}</dd>
        <dt className="text-[#7A8B82] uppercase tracking-widest">Status</dt>
        <dd className="text-[#0A1F16]">{card.status || "—"}</dd>
        <dt className="text-[#7A8B82] uppercase tracking-widest">Issued</dt>
        <dd className="text-[#0A1F16]">{card.issued_at ? new Date(card.issued_at).toLocaleString() : "—"}</dd>
        {card.provider_card_id && (
          <>
            <dt className="text-[#7A8B82] uppercase tracking-widest">Provider card ID</dt>
            <dd className="mono text-[10px] text-[#0A1F16] truncate">{card.provider_card_id}</dd>
          </>
        )}
        {card.provider_cardholder_id && (
          <>
            <dt className="text-[#7A8B82] uppercase tracking-widest">Cardholder ID</dt>
            <dd className="mono text-[10px] text-[#0A1F16] truncate">{card.provider_cardholder_id}</dd>
          </>
        )}
      </dl>

      <MigrateToNetworkPanel card={card} />
    </div>
  );
}


function MigrateToNetworkPanel({ card }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Only offer migration when the card is a wallet card AND active.
  const isWallet = !card.provider || card.provider === "wallet" || card.provider === "mock";
  if (!isWallet || card.status !== "active") return null;

  const migrate = async () => {
    if (!window.confirm(
      `Re-provision this $${(card.balance_usd || 0).toFixed(2)} wallet card on the Stripe Visa network?\n\n` +
      `• PAN, CVV, expiry, and QR will all change\n` +
      `• Balance, owner, top-ups, and history are preserved\n` +
      `• You'll need to re-deliver the card to ${card.receiver_email || "the receiver"} (the old QR no longer works)`
    )) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post(`/admin/cards/${card.id}/migrate-to-network`, {
        target_provider: "stripe",
      });
      setResult({ ok: true, ...r.data });
      toast.success(`Migrated to Stripe Visa · new •••• ${r.data.new_last4}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Migration failed";
      setResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-[#E5E0D8]" data-testid="migrate-to-network-panel">
      <p className="text-[10px] tracking-[0.2em] uppercase text-[#7A8B82] mb-1.5">Upgrade path</p>
      <p className="text-xs text-[#4A5C50] mb-2">
        Re-provision this wallet card on the Stripe Visa network. Balance + history preserved; PAN/CVV/expiry/QR replaced.
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={migrate}
        className="w-full text-xs font-medium px-3 py-2 rounded-lg border border-[#CBA35C] bg-[#CBA35C]/10 text-[#8C6B21] hover:bg-[#CBA35C]/20 disabled:opacity-50"
        data-testid="migrate-to-network-btn"
      >
        {busy ? "Migrating…" : "Migrate to Stripe Visa →"}
      </button>
      {result && (
        <div
          className={`mt-2 rounded-lg p-2.5 text-[11px] ${
            result.ok
              ? "bg-emerald-50 border border-emerald-200 text-emerald-900"
              : "bg-rose-50 border border-rose-200 text-rose-900"
          }`}
          data-testid="migrate-to-network-result"
        >
          {result.ok ? (
            <>
              <div className="font-medium">✓ Migrated</div>
              <div className="mono">{result.old_last4} → {result.new_last4} · exp {result.new_expiry}</div>
              <div className="mt-1 opacity-80">{result.note}</div>
              {result.redelivery && (
                <div className="mt-1.5 flex flex-wrap gap-x-3 text-[10px]" data-testid="single-migrate-redelivery">
                  {result.redelivery.email && (
                    <span className={result.redelivery.email.sent ? "text-emerald-700" : "text-rose-700"}>
                      {result.redelivery.email.sent ? "✓ emailed" : "✗ email failed"}
                    </span>
                  )}
                  {result.redelivery.sms && (
                    <span className={result.redelivery.sms.sent ? "text-emerald-700" : "text-rose-700"}>
                      {result.redelivery.sms.sent ? "✓ texted" : "✗ SMS failed"}
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="font-medium">✗ Migration failed</div>
              <div className="mt-1">{result.error}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


function EventStream({ events, card }) {
  const isNetwork = card.provider && card.provider !== "mock" && card.provider !== "wallet";

  if (!isNetwork) {
    return (
      <div
        className="mt-5 rounded-xl border border-dashed border-[#E5E0D8] bg-white p-5 text-xs text-[#4A5C50]"
        data-testid="card-issuance-drawer-empty-wallet"
      >
        <p className="font-medium text-[#0A1F16] mb-1">Closed-loop wallet card</p>
        <p>This card lives on the Zcardon wallet — receivers spend via QR-code charge against our balance, not a real card network. There are no Visa/Mastercard authorizations to stream.</p>
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div
        className="mt-5 rounded-xl border border-dashed border-[#E5E0D8] bg-white p-5 text-xs text-[#4A5C50]"
        data-testid="card-issuance-drawer-empty-network"
      >
        <p className="font-medium text-[#0A1F16] mb-1">No authorizations yet</p>
        <p>Stripe will stream <span className="mono">issuing_authorization.request</span> + <span className="mono">issuing_transaction.created</span> events here as soon as the receiver swipes/taps. Auto-refreshing every 8s.</p>
      </div>
    );
  }

  return (
    <div className="mt-5" data-testid="card-issuance-drawer-events">
      <p className="text-[10px] tracking-[0.2em] uppercase text-[#7A8B82] mb-2">
        {events.length} event{events.length === 1 ? "" : "s"}
      </p>
      <ul className="space-y-2">
        {events.map((e, i) => (
          <EventRow key={e.id || `evt-${i}`} event={e} />
        ))}
      </ul>
    </div>
  );
}


function EventRow({ event }) {
  const kind = event.kind || "unknown";
  const isAuth = kind === "stripe_issuing_authorization";
  const isTx = kind === "stripe_issuing_transaction";
  const decision = event.decision;

  const dotColor = (() => {
    if (isAuth && decision === "approved") return "bg-emerald-500";
    if (isAuth && decision === "declined") return "bg-rose-500";
    if (isTx) return "bg-[#CBA35C]";
    return "bg-stone-400";
  })();

  const headline = (() => {
    if (isAuth) {
      const amt = event.amount_cents != null ? `$${(Math.abs(event.amount_cents) / 100).toFixed(2)}` : "—";
      return `Authorization ${decision || "pending"} · ${amt}`;
    }
    if (isTx) {
      const amt = event.amount_usd != null ? `$${event.amount_usd.toFixed(2)}` : "—";
      return `Transaction settled · ${amt}`;
    }
    return kind;
  })();

  const subline = (() => {
    if (isAuth && event.reason) return `Reason: ${event.reason.replace(/_/g, " ")}`;
    if (isTx && event.new_balance_usd != null) return `New balance: $${event.new_balance_usd.toFixed(2)}`;
    return null;
  })();

  return (
    <li
      className="flex items-start gap-3 rounded-lg border border-[#E5E0D8] bg-white p-3"
      data-testid={`issuance-event-${event.id || event.auth_id || event.tx_id || "row"}`}
    >
      <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${dotColor}`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#0A1F16]">{headline}</p>
        {subline && <p className="text-xs text-[#4A5C50] mt-0.5">{subline}</p>}
        <p className="text-[11px] text-[#7A8B82] mt-1 mono">
          {event.ts ? new Date(event.ts).toLocaleString() : ""}
          {(event.auth_id || event.tx_id) && (
            <>
              {" · "}
              <span className="text-[10px]">{event.auth_id || event.tx_id}</span>
            </>
          )}
        </p>
      </div>
    </li>
  );
}

export default CardIssuanceDrawer;
